import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Database, Cpu, CreditCard, ShieldCheck, Coins, Server } from "lucide-react";
import { SmsDeliveryCard } from "@/components/SmsDeliveryCard";

/**
 * Tri-state health card:
 *   state = "ok"   → green  (all good)
 *          "warn" → amber  (soft flag off / approaching cap / not cross-pod)
 *          "down" → red    (critical dependency unreachable)
 *
 * We deliberately DON'T map "warn" to `ok=false` because the healthz JSON
 * treats critical vs advisory checks differently; folding them together
 * gave the confusing "All systems go / Security Config Down" combo the
 * testing agent flagged.
 */
const BADGES = {
  ok:   { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", ring: "", icon: CheckCircle2, label: "OK" },
  warn: { cls: "border-amber-500/40 text-amber-300 bg-amber-500/10",       ring: "ring-1 ring-amber-500/30", icon: AlertTriangle, label: "Warn" },
  down: { cls: "border-rose-500/40 text-rose-300 bg-rose-500/10",           ring: "ring-1 ring-rose-500/30", icon: XCircle, label: "Down" },
};

function StatusCard({ title, icon: Icon, state, detail, extra }) {
  const b = BADGES[state] || BADGES.down;
  const BadgeIcon = b.icon;
  return (
    <Card className={`border-white/10 bg-card p-4 ${b.ring}`} data-testid={`status-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-orange-400" /><div className="font-medium">{title}</div></div>
        <Badge variant="outline" className={b.cls} data-testid={`status-${title.toLowerCase().replace(/\s+/g, "-")}-badge`}>
          <BadgeIcon className="h-3 w-3 mr-1" /> {b.label}
        </Badge>
      </div>
      {detail && <div className="text-xs text-zinc-500 mt-2">{detail}</div>}
      {extra && <div className="mt-2">{extra}</div>}
    </Card>
  );
}

export default function StatusPage() {
  const [hz, setHz] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: b }] = await Promise.all([
        api.get("/healthz"),
        api.get("/admin/ai/budget-status").catch(() => ({ data: null })),
      ]);
      setHz(h); setBudget(b); setLastFetch(new Date());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const checks = hz?.checks || {};

  // Compute the per-card states first, then derive the overall banner from
  // the worst of those. This keeps banner + cards honest with each other.
  const cardStates = useMemo(() => {
    const s = {};
    s.mongo = checks.mongo?.ok ? "ok" : "down";
    s.stripe = checks.stripe?.ok ? "ok" : "down";
    s.llm = checks.llm_key?.ok ? "ok" : "down";
    // AI rate limit — Redis backend = ok; in-memory = warn (not cross-pod).
    if (!checks.ai_rate_limit) s.ai_rl = "down";
    else s.ai_rl = checks.ai_rate_limit.backend === "redis" ? "ok" : "warn";
    // Budget — over cap = down (blocking), approaching = warn, else ok.
    if (!budget || budget.budget_usd === 0) s.budget = "ok";
    else if (budget.over_budget) s.budget = "down";
    else if (budget.approaching_cap) s.budget = "warn";
    else s.budget = "ok";
    // Security config — soft flags. Any off = warn (not down). We don't
    // treat rate_limit_off as a hard failure because the env may
    // intentionally disable it for preview/dev.
    const sec = checks.security_config || {};
    const soft = ["rate_limit_enabled", "auth_rate_limit_enabled", "webhook_strict_mode"];
    const anyOff = soft.some((k) => sec[k] === false);
    s.security = anyOff ? "warn" : "ok";
    return s;
  }, [checks, budget]);

  const overallState = useMemo(() => {
    const values = Object.values(cardStates);
    if (values.includes("down")) return "down";
    if (values.includes("warn")) return "warn";
    return "ok";
  }, [cardStates]);

  const overallBadge = BADGES[overallState];
  const OverallIcon = overallBadge.icon;
  const overallLabel = overallState === "ok" ? "All systems go"
    : overallState === "warn" ? "Warnings" : "Degraded";

  return (
    <div>
      <PageHeader eyebrow="Ops" title="System Status" subtitle="Live health snapshot. Auto-refreshes every 30 seconds." />
      <div className="flex items-center gap-3 mb-4">
        <Badge variant="outline" className={overallBadge.cls} data-testid="overall-badge">
          <OverallIcon className="h-3 w-3 mr-1" /> {overallLabel}
        </Badge>
        {lastFetch && <span className="text-xs text-zinc-500">Last check {lastFetch.toLocaleTimeString()}</span>}
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="border-white/10" onClick={load} disabled={loading} data-testid="refresh-btn">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatusCard title="MongoDB" icon={Database} state={cardStates.mongo}
          detail={checks.mongo?.error || "Reachable"} />
        <StatusCard title="Stripe" icon={CreditCard} state={cardStates.stripe}
          detail={`Mode: ${checks.stripe?.mode || "?"}${checks.stripe?.note ? " · " + checks.stripe.note : ""}`} />
        <StatusCard title="LLM Key" icon={Cpu} state={cardStates.llm}
          detail={checks.llm_key?.ok ? "Emergent Universal Key present" : "EMERGENT_LLM_KEY missing"} />
        <StatusCard title="AI Rate Limit" icon={Server} state={cardStates.ai_rl}
          detail={checks.ai_rate_limit?.backend === "redis"
            ? `Backend: Redis · ${checks.ai_rate_limit.url || "connected"} · cap ${checks.ai_rate_limit.cap_per_minute}/min per user`
            : `Backend: ${checks.ai_rate_limit?.backend || "?"} · single-pod only · cap ${checks.ai_rate_limit?.cap_per_minute || "?"}/min`}
          extra={cardStates.ai_rl === "warn" && (
            <span className="text-[10px] text-amber-300">Not cross-pod — set REDIS_URL to enable multi-pod capping.</span>
          )}
        />
        {budget && (
          <StatusCard title="AI Budget" icon={Coins} state={cardStates.budget}
            detail={budget.budget_usd > 0
              ? `Spent $${budget.spend_usd.toFixed(2)} / $${budget.budget_usd.toFixed(0)} (${budget.pct_used || 0}%) — ${budget.calls_this_month} calls this month`
              : "Guardrail disabled (AI_MONTHLY_BUDGET_USD=0)"}
            extra={budget.budget_usd > 0 && (
              <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className={`h-full ${budget.over_budget ? "bg-rose-500" : budget.approaching_cap ? "bg-amber-500" : "bg-emerald-500"}`}
                     style={{ width: `${Math.min(100, budget.pct_used || 0)}%` }}
                     data-testid="budget-bar" />
              </div>
            )}
          />
        )}
        <StatusCard title="Security Config" icon={ShieldCheck} state={cardStates.security}
          detail={cardStates.security === "ok"
            ? "All hardening flags are on."
            : "One or more soft-hardening flags are OFF — safe for dev/preview, revisit for prod."}
          extra={
            <div className="flex flex-wrap gap-1 mt-1">
              {[
                ["Rate limit", checks.security_config?.rate_limit_enabled],
                ["Auth throttle", checks.security_config?.auth_rate_limit_enabled],
                ["Webhook strict", checks.security_config?.webhook_strict_mode],
              ].map(([label, on]) => (
                <span key={label} className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-amber-500/30 text-amber-300 bg-amber-500/10"}`}>
                  {label}: {on ? "on" : "off"}
                </span>
              ))}
              <span className="text-[10px] text-zinc-500 px-2 py-0.5">env: {checks.security_config?.sentry_env || "n/a"}</span>
            </div>
          }
        />
        <SmsDeliveryCard />
      </div>

      {hz && (
        <details className="mt-6">
          <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300">Raw JSON</summary>
          <pre className="text-[10px] text-zinc-400 mt-2 p-3 rounded border border-white/10 bg-black/40 overflow-x-auto">{JSON.stringify({ healthz: hz, budget }, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}
